import React from "react";

/**
 * Parametric instrument silhouette. Stylised, not photoreal. Reacts to:
 *  - type: "bisernica"|"brac"|"bugarija"|"celo"|"berda"|"mandolina"
 *  - config: { wood_top_hex, wood_back_hex, inlay, finish, engraving_text }
 *
 * The shapes share a common base SVG path; proportions (body width vs neck
 * length, number of strings) come from `SHAPE_VARIANT`. Finish is applied via
 * SVG filters; inlay swaps the rosette glyph.
 */

const SHAPE_VARIANT = {
  bisernica: { bodyW: 80, bodyH: 110, neckLen: 110, strings: 5, headW: 30, bodyShape: "pear" },
  brac:       { bodyW: 100, bodyH: 130, neckLen: 130, strings: 4, headW: 34, bodyShape: "pear" },
  bugarija:   { bodyW: 110, bodyH: 145, neckLen: 130, strings: 4, headW: 36, bodyShape: "pear" },
  celo:       { bodyW: 140, bodyH: 175, neckLen: 130, strings: 4, headW: 40, bodyShape: "wide" },
  berda:      { bodyW: 175, bodyH: 220, neckLen: 130, strings: 4, headW: 46, bodyShape: "wide" },
  mandolina:  { bodyW: 95, bodyH: 110, neckLen: 110, strings: 8, headW: 32, bodyShape: "teardrop" },
};

function bodyPath(shape, cx, cy, w, h) {
  // Different body silhouettes via cubic curves.
  if (shape === "teardrop") {
    return `
      M ${cx},${cy - h / 2}
      C ${cx + w * 0.6},${cy - h / 2}, ${cx + w / 2},${cy + h / 2}, ${cx},${cy + h / 2}
      C ${cx - w / 2},${cy + h / 2}, ${cx - w * 0.6},${cy - h / 2}, ${cx},${cy - h / 2}
      Z`;
  }
  if (shape === "wide") {
    return `
      M ${cx},${cy - h / 2}
      C ${cx + w * 0.7},${cy - h * 0.45}, ${cx + w * 0.55},${cy + h * 0.55}, ${cx},${cy + h / 2}
      C ${cx - w * 0.55},${cy + h * 0.55}, ${cx - w * 0.7},${cy - h * 0.45}, ${cx},${cy - h / 2}
      Z`;
  }
  // pear (default tamburitza)
  return `
    M ${cx},${cy - h / 2}
    C ${cx + w * 0.45},${cy - h * 0.35}, ${cx + w * 0.55},${cy + h * 0.5}, ${cx},${cy + h / 2}
    C ${cx - w * 0.55},${cy + h * 0.5}, ${cx - w * 0.45},${cy - h * 0.35}, ${cx},${cy - h / 2}
    Z`;
}

function RosetteGlyph({ inlay, cx, cy, r = 14 }) {
  if (inlay === "none" || !inlay) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={r * 0.55} fill="#1A0F08" />
        <circle cx={cx} cy={cy} r={r * 0.55} fill="none" stroke="#3D2418" strokeWidth="1" />
      </g>
    );
  }
  if (inlay === "mop_rosette") {
    // 12-petal rosette in iridescent off-white
    const petals = Array.from({ length: 12 }, (_, i) => {
      const a = (i * 360) / 12;
      return (
        <ellipse
          key={i}
          cx={cx}
          cy={cy - r * 0.8}
          rx="1.6"
          ry={r * 0.45}
          fill="#F8EFE3"
          opacity="0.9"
          transform={`rotate(${a} ${cx} ${cy})`}
        />
      );
    });
    return (
      <g>
        {petals}
        <circle cx={cx} cy={cy} r={r * 0.45} fill="#1A0F08" />
        <circle cx={cx} cy={cy} r={r * 0.45} fill="none" stroke="#C97B5C" strokeWidth="0.6" />
      </g>
    );
  }
  // custom_engraving — ornate floral arc
  return (
    <g>
      <circle cx={cx} cy={cy} r={r * 0.5} fill="#1A0F08" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#C97B5C" strokeWidth="1.4" />
      <circle cx={cx} cy={cy} r={r * 1.4} fill="none" stroke="#C97B5C" strokeWidth="0.6" strokeDasharray="2 3" />
    </g>
  );
}

export default function InstrumentSVG({
  type = "bisernica",
  config = {},
  engravingText = "",
  className = "",
  testid = "instrument-svg",
}) {
  const v = SHAPE_VARIANT[type] || SHAPE_VARIANT.bisernica;
  const woodTop = config.wood_top_hex || "#E6CFA1";
  const woodBack = config.wood_back_hex || "#6B4226";
  const finish = config.finish || "satin";
  const inlay = config.inlay || "none";

  // SVG canvas
  const W = 220;
  const H = 380;
  const bodyCx = W / 2;
  const bodyCy = H - v.bodyH / 2 - 18;
  const neckTopY = bodyCy - v.bodyH / 2;
  const headTopY = neckTopY - v.neckLen;
  const stringSpacing = (v.bodyW * 0.42) / Math.max(1, v.strings - 1);
  const stringStartX = bodyCx - (stringSpacing * (v.strings - 1)) / 2;

  const filterId = `f-${type}`;
  const gradientId = `g-${type}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`w-full h-full ${className}`}
      data-testid={testid}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${type} preview`}
    >
      <defs>
        <radialGradient id={gradientId} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={lighten(woodTop, 0.18)} />
          <stop offset="65%" stopColor={woodTop} />
          <stop offset="100%" stopColor={darken(woodTop, 0.22)} />
        </radialGradient>
        {finish === "high_gloss" && (
          <filter id={filterId} x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="0.3" />
          </filter>
        )}
        {finish === "antique" && (
          <filter id={filterId} x="-5%" y="-5%" width="110%" height="110%">
            <feColorMatrix
              type="matrix"
              values="0.55 0.5 0.35 0 0
                      0.45 0.5 0.3 0 0
                      0.3 0.35 0.25 0 0
                      0 0 0 1 0"
            />
          </filter>
        )}
      </defs>

      <g filter={finish === "satin" ? undefined : `url(#${filterId})`}>
        {/* Body shadow */}
        <ellipse cx={bodyCx + 3} cy={bodyCy + v.bodyH / 2 + 4} rx={v.bodyW / 1.7} ry={6} fill="#000" opacity="0.18" />

        {/* Back rim (slightly larger so it shows as a rim around the top) */}
        <path
          d={bodyPath(v.bodyShape, bodyCx, bodyCy, v.bodyW + 6, v.bodyH + 8)}
          fill={woodBack}
          stroke={darken(woodBack, 0.2)}
          strokeWidth="1.2"
        />

        {/* Soundboard (top) */}
        <path
          d={bodyPath(v.bodyShape, bodyCx, bodyCy, v.bodyW, v.bodyH)}
          fill={`url(#${gradientId})`}
          stroke={darken(woodTop, 0.25)}
          strokeWidth="0.6"
        />

        {/* Subtle grain lines on the top */}
        {Array.from({ length: 6 }).map((_, i) => (
          <line
            key={i}
            x1={bodyCx - v.bodyW * 0.35 + i * (v.bodyW * 0.14)}
            y1={bodyCy - v.bodyH * 0.45}
            x2={bodyCx - v.bodyW * 0.35 + i * (v.bodyW * 0.14)}
            y2={bodyCy + v.bodyH * 0.45}
            stroke={darken(woodTop, 0.12)}
            strokeWidth="0.25"
            opacity="0.5"
          />
        ))}

        {/* Sound hole + rosette */}
        <RosetteGlyph inlay={inlay} cx={bodyCx} cy={bodyCy - v.bodyH * 0.08} r={v.bodyW * 0.13} />

        {/* Bridge */}
        <rect
          x={bodyCx - v.bodyW * 0.18}
          y={bodyCy + v.bodyH * 0.18}
          width={v.bodyW * 0.36}
          height="4"
          rx="1"
          fill={darken(woodBack, 0.15)}
        />

        {/* Neck */}
        <rect
          x={bodyCx - 7}
          y={headTopY + 6}
          width="14"
          height={v.neckLen}
          rx="2"
          fill={woodBack}
          stroke={darken(woodBack, 0.25)}
          strokeWidth="0.8"
        />
        {/* Fretboard accent */}
        <rect
          x={bodyCx - 5.5}
          y={headTopY + 6}
          width="11"
          height={v.neckLen - 2}
          fill={darken(woodBack, 0.3)}
        />
        {/* Frets */}
        {Array.from({ length: 7 }).map((_, i) => (
          <line
            key={i}
            x1={bodyCx - 5.5}
            x2={bodyCx + 5.5}
            y1={headTopY + 12 + i * (v.neckLen * 0.13)}
            y2={headTopY + 12 + i * (v.neckLen * 0.13)}
            stroke="#D4B385"
            strokeWidth="0.5"
          />
        ))}

        {/* Headstock */}
        <path
          d={`M ${bodyCx - v.headW / 2},${headTopY} 
              L ${bodyCx + v.headW / 2},${headTopY}
              L ${bodyCx + v.headW / 2 - 4},${headTopY + 16}
              L ${bodyCx - v.headW / 2 + 4},${headTopY + 16} Z`}
          fill={darken(woodBack, 0.08)}
          stroke={darken(woodBack, 0.3)}
          strokeWidth="0.8"
        />
        {/* Tuning pegs */}
        {Array.from({ length: v.strings }).map((_, i) => {
          const row = i % 2;
          const col = Math.floor(i / 2);
          return (
            <circle
              key={i}
              cx={bodyCx - v.headW / 2 + 6 + col * 6}
              cy={headTopY + 5 + row * 8}
              r="1.5"
              fill="#8B5A3C"
            />
          );
        })}

        {/* Strings */}
        {Array.from({ length: v.strings }).map((_, i) => (
          <line
            key={i}
            x1={stringStartX + i * stringSpacing}
            y1={headTopY + 10}
            x2={stringStartX + i * stringSpacing}
            y2={bodyCy + v.bodyH * 0.18}
            stroke="#C0A77A"
            strokeWidth="0.5"
            opacity="0.85"
          />
        ))}

        {/* Highlight on gloss */}
        {finish === "high_gloss" && (
          <ellipse
            cx={bodyCx - v.bodyW * 0.2}
            cy={bodyCy - v.bodyH * 0.15}
            rx={v.bodyW * 0.18}
            ry={v.bodyH * 0.12}
            fill="#FFF"
            opacity="0.18"
          />
        )}
      </g>

      {/* Engraving text below the bridge */}
      {engravingText && (
        <text
          x={bodyCx}
          y={bodyCy + v.bodyH * 0.36}
          textAnchor="middle"
          fontFamily="'Playfair Display', serif"
          fontStyle="italic"
          fontSize={Math.min(13, 14 - engravingText.length * 0.15)}
          fill={darken(woodTop, 0.55)}
          opacity="0.8"
        >
          {engravingText}
        </text>
      )}
    </svg>
  );
}

// --- tiny color helpers (hex -> rgb -> hex) ---
function _hexToRgb(hex) {
  const v = hex.replace("#", "");
  const r = parseInt(v.substring(0, 2), 16);
  const g = parseInt(v.substring(2, 4), 16);
  const b = parseInt(v.substring(4, 6), 16);
  return [r, g, b];
}
function _rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");
}
function lighten(hex, amt = 0.1) {
  const [r, g, b] = _hexToRgb(hex);
  return _rgbToHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
function darken(hex, amt = 0.1) {
  const [r, g, b] = _hexToRgb(hex);
  return _rgbToHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}
